import React, { useMemo, forwardRef } from 'react';
import { CircularProgress, FormControl, FormHelperText, InputLabel } from '@mui/material';
import ReactSelect from 'react-select';
import type { EisRuleTemplate } from '../_types';

export type EisTemplatePickerProps = {
  id: string;
  label?: string;
  value?: number | null;
  onChange: (value: number | null) => void;
  options: EisRuleTemplate[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: string;
  sx?: any;
  isLoading?: boolean;
  onScrollToBottom?: () => void;
  onSearchChange?: (value: string) => void;
};

type TemplateOption = { value: number; label: string };

const LoadingIndicator = () => (
  <CircularProgress
    size={16}
    sx={{ mr: 1 }}
  />
);

const EisTemplatePicker = forwardRef<HTMLDivElement, EisTemplatePickerProps>(
  (
    {
      id,
      label,
      value,
      onChange,
      options: templates,
      placeholder,
      disabled,
      required,
      error,
      helperText,
      size,
      sx,
      isLoading,
      onScrollToBottom,
      onSearchChange,
    },
    ref,
  ) => {
    const options: TemplateOption[] = useMemo(
      () => templates.map((tpl) => ({ value: tpl.id, label: tpl.name })),
      [templates],
    );

    const selectedValue = useMemo(
      () => options.find((x) => x.value === value) ?? null,
      [value, options],
    );

    return (
      <FormControl
        fullWidth
        error={error}
        className={error ? 'Mui-error' : ''}
        sx={sx}
        ref={ref}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        <ReactSelect
          name={id}
          inputId={id}
          value={selectedValue}
          options={options}
          placeholder={placeholder}
          isDisabled={disabled}
          isLoading={isLoading}
          components={{ LoadingIndicator }}
          classNamePrefix='react-select'
          menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          onChange={(newValue) => {
            if (newValue && typeof newValue === 'object' && 'value' in newValue) {
              onChange((newValue as TemplateOption).value);
            } else {
              onChange(null);
            }
          }}
          onMenuScrollToBottom={onScrollToBottom}
          onInputChange={(inputValue, { action }) => {
            if (action === 'input-change' && onSearchChange) {
              onSearchChange(inputValue);
            }
          }}
        />
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  },
);

EisTemplatePicker.displayName = 'EisTemplatePicker';

export default EisTemplatePicker;
